/**
 * PublicRoadmap — marketing site /roadmap page.
 *
 * Pulls live data from `/api/marketing/roadmap`. The admin manages the
 * actual cards via the in-app /app/roadmap kanban (admin-only), and toggles
 * `is_public=true` on the cards they want to surface here. We render those
 * cards grouped by their kanban column (Backlog / Planned / In Progress /
 * Shipped) so visitors see what the Bloq team is working on right now.
 *
 * No fallback hardcoded list — when no public cards exist yet, we show a
 * friendly empty state. The founder said "I want that roadmap to sync to
 * the page" so the marketing surface MUST be live, not duplicated copy.
 */
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import api from "@/lib/api";

export default function PublicRoadmap() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/marketing/roadmap")
      .then((r) => alive && setGroups(r.data?.groups || []))
      .catch(() => alive && setGroups([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />

      <section className="bloq-section">
        <div className="bloq-container max-w-5xl">
          <div className="text-center mb-16 bloq-fade-up">
            <p className="bloq-eyebrow">Roadmap</p>
            <h1 className="bloq-display text-5xl sm:text-6xl mt-3 mb-5">
              What we're building <span className="bloq-gradient-text">right now.</span>
            </h1>
            <p className="text-[#4a4a52] text-lg max-w-2xl mx-auto leading-relaxed">
              The Bloq team's actual working roadmap — synced live from inside the app.
              Want to push something up the list? <a href="/fund-a-feature" className="text-[#005e7a] underline font-medium">Fund it.</a>
            </p>
          </div>

          {loading ? (
            <p className="text-center text-[#82828c]">Loading…</p>
          ) : groups.length === 0 ? (
            <div className="text-center bloq-card p-12 max-w-2xl mx-auto">
              <Sparkles className="w-12 h-12 text-[#005e7a] mx-auto mb-4" />
              <p className="bloq-headline text-2xl mb-2">More coming soon.</p>
              <p className="text-[#4a4a52]">We're heads-down shipping. Check back in a few days.</p>
            </div>
          ) : (
            <div className="space-y-12" data-testid="public-roadmap-groups">
              {groups.map((g) => (
                <div key={g.name} className="bloq-fade-up" data-testid={`roadmap-group-${g.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="bloq-eyebrow mb-4">{g.name}</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {g.items.map((item) => (
                      <div key={item.card_id} className="bloq-card p-6 flex flex-col gap-2">
                        <p className="bloq-headline text-lg leading-snug">{item.title}</p>
                        {item.target_quarter && (
                          <span className="bloq-pill text-[10px] uppercase tracking-wider self-start"
                                style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
                            {item.target_quarter}
                          </span>
                        )}
                        {item.description && (
                          <p className="text-sm text-[#4a4a52] leading-relaxed">{item.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
